import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import { Loader2, TrendingUp, TrendingDown, Minus, Download } from "lucide-react";

export default function SourceROIPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/pipeline/source-roi`);
      setData(res.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCSV = () => {
    if (!data?.sources) return;
    const headers = ["Source,Tier,Calls Used,Calls Remaining,Monthly Limit,Cost/Call,Data Points,ROI Score,Trend,Circuit Breaker"];
    const rows = data.sources.map(s =>
      `${s.source_name},${s.tier},${s.calls_used},${s.calls_remaining ?? "N/A"},${s.monthly_limit ?? "Unlimited"},${s.cost_per_call},${s.data_points_added},${s.roi_score},${s.trend},${s.circuit_breaker ? "TRIPPED" : "OK"}`
    );
    const csv = [...headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `source_roi_${data.month || "report"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-4">Unable to load ROI data</p>;

  const sources = data.sources || [];

  return (
    <div className="space-y-4" data-testid="source-roi-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{sources.length} sources tracked — {data.month}</p>
        <button
          onClick={exportCSV}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-700 text-white hover:bg-slate-800 transition-colors"
          data-testid="export-csv-btn"
        >
          <Download className="w-3 h-3" />Export CSV
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="source-roi-table">
          <thead>
            <tr className="border-b-2 border-slate-200 text-slate-500">
              <th className="text-left py-2 px-2">Source</th>
              <th className="text-center py-2 px-2">Tier</th>
              <th className="text-center py-2 px-2">Used</th>
              <th className="text-center py-2 px-2">Remaining</th>
              <th className="text-center py-2 px-2">Limit</th>
              <th className="text-center py-2 px-2">Cost</th>
              <th className="text-center py-2 px-2">Data Pts</th>
              <th className="text-center py-2 px-2">ROI</th>
              <th className="text-center py-2 px-2">Trend</th>
              <th className="text-center py-2 px-2">Circuit</th>
            </tr>
          </thead>
          <tbody>
            {sources.map(s => {
              const roiColor = s.roi_score >= 0.5 ? "bg-emerald-50 text-emerald-700" : s.roi_score > 0 && s.roi_score < 0.1 ? "bg-red-50 text-red-700" : "";
              const tierColors = { A: "bg-sky-100 text-sky-700", B: "bg-amber-100 text-amber-700", C: "bg-slate-100 text-slate-600" };
              return (
                <tr key={s.source_name} className={`border-b border-slate-100 hover:bg-slate-50 ${roiColor}`} data-testid={`roi-row-${s.source_name}`}>
                  <td className="py-2 px-2 font-medium text-slate-700">{s.source_name}</td>
                  <td className="py-2 px-2 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${tierColors[s.tier] || ""}`}>{s.tier}</span>
                  </td>
                  <td className="py-2 px-2 text-center">{s.calls_used}</td>
                  <td className="py-2 px-2 text-center">{s.calls_remaining ?? "N/A"}</td>
                  <td className="py-2 px-2 text-center">{s.monthly_limit ?? "Unlimited"}</td>
                  <td className="py-2 px-2 text-center text-slate-500">{s.cost_per_call}</td>
                  <td className="py-2 px-2 text-center font-semibold">{s.data_points_added}</td>
                  <td className="py-2 px-2 text-center font-bold">{s.roi_score.toFixed(3)}</td>
                  <td className="py-2 px-2 text-center">
                    {s.trend === "up" ? <TrendingUp className="w-4 h-4 text-emerald-500 mx-auto" /> :
                     s.trend === "down" ? <TrendingDown className="w-4 h-4 text-red-500 mx-auto" /> :
                     <Minus className="w-4 h-4 text-slate-400 mx-auto" />}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${s.circuit_breaker ? "bg-red-500" : "bg-emerald-500"}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
